import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileInput } from 'components/FileInput/FileInput';
import { Dropdown } from 'components/Forms/Dropdown/Dropdown';
import { TextInput } from 'components/TextInput/TextInput';
import months from 'data/months';
import moment from 'moment-timezone';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { maxFileUploadSize } from 'utils/constants';
import { removeNonNumericCharacters } from 'utils/format-number';
import { useResetTwoFactorMutation } from '../api/auth-api';
import messages from './messages';
import { ResetTwoFactorRequestViewState } from './types/ResetTwoFactorRequestViewState';
import Button from 'components/Button/Button';

export const Route = createFileRoute('/auth/reset-two-factor/')({
  component: ResetTwoFactorRequestView,
});

export default function ResetTwoFactorRequestView() {
  const [state, setState] = React.useState<ResetTwoFactorRequestViewState>({});
  const [isSelfieValid, setIsSelfieValid] = React.useState<boolean | string>(
    false,
  );
  const [isPassportValid, setIsPassportValid] = React.useState<
    boolean | string
  >(false);
  const { language } = useSelector(
    (rootState: RootState) => rootState.language,
  );
  const navigate = useNavigate();
  const [resetTwoFactor, resetTwoFactorData] = useResetTwoFactorMutation();

  function monthNumber(month: any) {
    const monthNumberFormat = moment()
      .month(month || '')
      .format('MM');

    return Number(monthNumberFormat) - 1;
  }

  function isValidDate(day: any, month: any, year: any) {
    const date = moment([
      Number(year || ''),
      Number(monthNumber(month)),
      Number(day),
    ]);

    return date.isValid();
  }

  function isStateValid(value: ResetTwoFactorRequestViewState): boolean {
    if (value.approximateBalance == null) return false;
    if (!value.dayOfBirth) return false;
    if (!value.dayOfFirstDeposit) return false;
    if (!value.firstName) return false;
    if (!value.lastName) return false;
    if (!value.monthOfBirth) return false;
    if (!value.monthOfFirstDeposit) return false;
    if (!value.passport) return false;
    if (!value.phoneNumber) return false;
    if (!value.selfie) return false;
    if (!value.yearOfBirth) return false;
    if (!value.yearOfFirstDeposit) return false;
    if (value.passportFileNameTooLong) return false;
    if (value.selfieFileNameTooLong) return false;
    if (!isSelfieValid) return false;
    if (!isPassportValid) return false;
    if (value?.selfie?.size > maxFileUploadSize) return false;
    if (value?.passport?.size > maxFileUploadSize) return false;

    if (!isValidDate(value.dayOfBirth, value.monthOfBirth, value.yearOfBirth))
      return false;
    if (
      !isValidDate(
        value.dayOfFirstDeposit,
        value.monthOfFirstDeposit,
        value.yearOfFirstDeposit,
      )
    )
      return false;

    return true;
  }

  function convertToOptions(values: string[]) {
    return values.map(x => {
      return { value: x, name: x };
    });
  }

  const promptSelfieErrorMessage =
    (!isSelfieValid || (state?.selfie?.size || 0) > maxFileUploadSize) &&
    state?.selfie?.name;

  const promptPassportErrorMessage =
    (!isPassportValid || (state?.passport?.size || 0) > maxFileUploadSize) &&
    state?.passport?.name;

  const resetTwoFactorRequestMessages =
    messages[language].resetTwoFactorRequest;
  const { warningMessages } = messages[language];
  const { errorMessages } = messages[language];
  const monthOptions = convertToOptions(months);

  async function onResetTwoFactor(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid(state)) return;

    const dateOfBirth = moment([
      Number(state.yearOfBirth || ''),
      Number(monthNumber(state.monthOfBirth)),
      Number(state.dayOfBirth),
    ]).format('YYYY-MM-DD');

    const firstDepositDate = moment([
      Number(state.yearOfFirstDeposit || ''),
      Number(monthNumber(state.monthOfFirstDeposit)),
      Number(state.dayOfFirstDeposit),
    ]).format('YYYY-MM-DD');

    const res = await resetTwoFactor({
      balance: state.approximateBalance ?? 0,
      dateOfBirth,
      firstDepositDate,
      firstName: state.firstName!,
      lastName: state.lastName!,
      phoneNumber: state.phoneNumber!,
      idDocument: state.passport!,
      selfieDocument: state.selfie!,
    });

    if (!res.error) {
      navigate({ from: Route.fullPath, to: './submitted' });
    }
  }

  return (
    <div>
      <form onSubmit={onResetTwoFactor}>
        <div className="flex flex-col bg-ghost-white">
          <div className="sm:max-w-xl sm:mx-auto sm:w-full">
            <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
              {resetTwoFactorRequestMessages.heading}
            </h2>
            <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
              {resetTwoFactorRequestMessages.subHeading}
            </p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="m-6 mt-2">
              <TextInput
                name="firstName"
                type="firstName"
                label={resetTwoFactorRequestMessages.firstNameLabel}
                onChange={value => {
                  setState({ ...state, firstName: value as string });
                }}
              />
              <TextInput
                name="lastName"
                type="lastName"
                label={resetTwoFactorRequestMessages.lastNameLabel}
                onChange={value => {
                  setState({ ...state, lastName: value as string });
                }}
              />

              <div className="pt-3">
                <label className="block font-normal leading-5 text-gray-900">
                  {resetTwoFactorRequestMessages.dateOfBirthLabel}
                </label>
                <div className="mt-1 relative rounded-md mb-4">
                  <div className="-mt-px flex">
                    <div className="flex-1 min-w-0 w-1/4 mr-2">
                      <input
                        data-browsing-ignore
                        className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full focus:border-gray-200  py-2 px-3"
                        type="text"
                        pattern="[0-9]*"
                        placeholder={
                          resetTwoFactorRequestMessages.dayOfBirthLabel
                        }
                        onChange={value => {
                          setState({
                            ...state,
                            dayOfBirth: +value.target.value,
                          });
                        }}
                        maxLength={2}
                        inputMode="numeric"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Dropdown
                        autocomplete
                        options={monthOptions}
                        value={state.monthOfBirth}
                        placeholder={
                          resetTwoFactorRequestMessages.monthOfBirthLabel
                        }
                        onChange={value => {
                          setState({ ...state, monthOfBirth: value as string });
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 ml-2">
                      <input
                        data-browsing-ignore
                        className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full focus:border-gray-200   py-2 px-3"
                        type="text"
                        pattern="[0-9]*"
                        placeholder={
                          resetTwoFactorRequestMessages.yearOfBirthLabel
                        }
                        onChange={value => {
                          setState({
                            ...state,
                            yearOfBirth: +value.target.value,
                          });
                        }}
                        maxLength={4}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <TextInput
                name="phoneNumber"
                type="tel"
                label={resetTwoFactorRequestMessages.phoneNumberLabel}
                pattern="[0-9]*"
                mt-4
                onChange={value => {
                  setState({ ...state, phoneNumber: value as string });
                }}
              />

              <div className="pt-3">
                <label className="block font-normal leading-5 text-gray-900">
                  {resetTwoFactorRequestMessages.approximateFirstDepositLabel}
                </label>
                <div className="mt-1 relative rounded-md mb-4">
                  <div className="-mt-px flex">
                    <div className="flex-1 min-w-0 w-1/4 mr-2">
                      <input
                        data-browsing-ignore
                        className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full focus:border-gray-200   py-2 px-3"
                        type="text"
                        pattern="[0-9]*"
                        placeholder={
                          resetTwoFactorRequestMessages.dayOfBirthLabel
                        }
                        onChange={value => {
                          setState({
                            ...state,
                            dayOfFirstDeposit: +value.target.value,
                          });
                        }}
                        maxLength={2}
                        inputMode="numeric"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Dropdown
                        autocomplete
                        options={monthOptions}
                        value={state.monthOfFirstDeposit}
                        placeholder={
                          resetTwoFactorRequestMessages.monthOfBirthLabel
                        }
                        onChange={value => {
                          setState({
                            ...state,
                            monthOfFirstDeposit: value as string,
                          });
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 ml-2">
                      <input
                        data-browsing-ignore
                        className="bg-transparent border-gray-200 border-0 border-b-2 form-input rounded-none sm:leading-5 sm:text-md w-full focus:border-gray-200   py-2 px-3"
                        type="text"
                        pattern="[0-9]*"
                        placeholder={
                          resetTwoFactorRequestMessages.yearOfBirthLabel
                        }
                        onChange={value => {
                          setState({
                            ...state,
                            yearOfFirstDeposit: +value.target.value,
                          });
                        }}
                        maxLength={4}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <TextInput
                name="approximateBalance"
                type="approximateBalance"
                mt-4
                label={resetTwoFactorRequestMessages.approximateBalance}
                onChange={value => {
                  const numericValue = removeNonNumericCharacters(value);
                  setState({
                    ...state,
                    approximateBalance: numericValue as number,
                  });
                }}
              />
              <div className="py-4">
                <h2 className="font-semibold leading-9 mt-2 text-1.06x1 text-black tracking-wide mb-4">
                  {resetTwoFactorRequestMessages.uploadPassportSubHeading}
                </h2>
                <FileInput
                  name="passport"
                  isPrimary
                  primaryColour
                  fileUploadHoverIgnore
                  warning={state?.passportFileNameTooLong}
                  warningMessage={
                    state.passportFileNameTooLong
                      ? warningMessages.fileNameTooLong
                      : ''
                  }
                  fileUploadName={state?.passport?.name}
                  hasReducedPadding
                  label={resetTwoFactorRequestMessages.uploadPassportLabel}
                  buttonText={resetTwoFactorRequestMessages.chooseFileLabel}
                  setIsFileStateValid={setIsPassportValid}
                  onChange={(value: FileList) => {
                    const fileNameTooLong = value[0]?.name?.length > 50;
                    setState({
                      ...state,
                      passport: value[0] || null,
                      passportFileNameTooLong: fileNameTooLong,
                    });
                  }}
                />
                {promptPassportErrorMessage && (
                  <div>
                    <label className="text-center font-normal text-red-600">
                      {errorMessages.fileUploadError}
                    </label>
                  </div>
                )}
                <div
                  className="mt-2 block font-normal leading-5 text-gray-500"
                  dangerouslySetInnerHTML={{
                    __html:
                      resetTwoFactorRequestMessages.fileUploadRequirements,
                  }}
                />
              </div>
              <div className="py-4">
                <h2 className="font-semibold leading-9 mt-2 text-1.06x1 text-black tracking-wide mb-4">
                  {resetTwoFactorRequestMessages.uploadSelfieSubHeading}
                </h2>
                <FileInput
                  name="selfie"
                  isPrimary
                  primaryColour
                  fileUploadHoverIgnore
                  warning={state.selfieFileNameTooLong}
                  warningMessage={
                    state?.selfieFileNameTooLong
                      ? warningMessages.fileNameTooLong
                      : ''
                  }
                  fileUploadName={state?.selfie?.name}
                  hasReducedPadding
                  label={resetTwoFactorRequestMessages.uploadSelfieLabel}
                  buttonText={resetTwoFactorRequestMessages.chooseFileLabel}
                  setIsFileStateValid={setIsSelfieValid}
                  onChange={(value: FileList) => {
                    const fileNameTooLong = value[0]?.name?.length > 50;
                    setState({
                      ...state,
                      selfie: value[0] || null,
                      selfieFileNameTooLong: fileNameTooLong,
                    });
                  }}
                />
                {promptSelfieErrorMessage && (
                  <div>
                    <label className="text-center font-normal text-red-600">
                      {errorMessages.fileUploadError}
                    </label>
                  </div>
                )}
                <div
                  className="mt-2 block font-normal leading-5 text-gray-500"
                  dangerouslySetInnerHTML={{
                    __html:
                      resetTwoFactorRequestMessages.fileUploadRequirements,
                  }}
                />
              </div>
              <div className="py-4">
                <span className="block w-full rounded-md">
                  <Button
                    main
                    full
                    id="button:twofactor:reset"
                    onClick={onResetTwoFactor}
                    disabled={
                      !isStateValid(state) ||
                      (resetTwoFactorData.isLoading && false)
                    }
                    loading={resetTwoFactorData.isLoading}
                    type="submit">
                    {resetTwoFactorRequestMessages.submitButton}
                  </Button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
